// src/components/ChatView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Loader, Send, Copy, Check } from 'lucide-react';
import { IoSend, IoGlobe, IoMic, IoStop } from 'react-icons/io5';

const ChatView = ({ messages, userInput, setUserInput, isAnswering, handleSendMessage, handleCopy, copiedIndex }) => {
  const { t, i18n } = useTranslation(); // ✅ Safe to use here — i18next supports hooks in any component
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);
  const stripMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Remove code blocks first (including language specifier)
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Remove images
      .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      // Remove bold
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      // Remove italic
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove strikethrough
      .replace(/~~(.*?)~~/g, '$1')
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove blockquotes
      .replace(/^\s{0,3}>\s?/gm, '')
      // Remove horizontal rules
      .replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
      // Remove list markers
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Clean up multiple newlines
      .replace(/\n{3,}/g, '\n\n')
      // Trim whitespace
      .trim();
  };   

  const getRecognitionLanguage = (lang) => {
        const langMap = {
            'en': 'en-US',
            'fr': 'fr-FR',
            'de': 'de-DE'
        };
        return langMap[lang] || 'en-US';
    };
    
    // Initialize speech recognition
    useEffect(() => {
        // Check if browser supports speech recognition
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (SpeechRecognition) {
            setIsSupported(true);
            const recognition = new SpeechRecognition();
            
            // Configure recognition
            recognition.continuous = true; // Keep listening until stopped
            recognition.interimResults = true; // Show results as user speaks
            recognition.lang = getRecognitionLanguage(i18n.language);// Change to your preferred language
            
            // Handle results
            recognition.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';
                
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    } else {
                        interimTranscript += transcript;
                    }
                }
                
                // Update content with final transcript
                if (finalTranscript) {
                    setUserInput(prev => prev + finalTranscript);
                }
            };
            
            // Handle errors
            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                
                if (event.error === 'not-allowed') {
                    alert('Microphone access denied. Please enable microphone permissions.');
                } else if (event.error === 'no-speech') {
                    // Silently handle no speech detected
                    console.log('No speech detected');
                }
            };
            
            // Handle end of recognition
            recognition.onend = () => {
                setIsListening(false);
            };
            
            recognitionRef.current = recognition;
        }
        
        // Cleanup
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) return;
        
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                recognitionRef.current.start();
                setIsListening(true);
            } catch (error) {
                console.error('Error starting recognition:', error);
            }
        }
    };



  return (
    <div className="flex flex-col h-full bg-brand-white">
      {/* Message List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xl lg:max-w-3xl px-4 py-2 rounded-lg relative group ${
                msg.role === 'user'
                  ? 'bg-gray-200 text-brand-dark'
                  : 'bg-brand-white text-brand-dark border border-slate-100 shadow-sm'
              }`}
            >
	      {msg.role !== 'user' && (
                <button
                    onClick={() => handleCopy(stripMarkdown(msg.content), index)}
                    className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Copy"
                >
                    {copiedIndex === index ? (
                        <Check size={16} className="text-green-500" />
                    ) : (
                        <Copy size={16} />
                    )}
                </button>
              )}

              <div className="prose prose-invert max-w-none prose-p:my-2 ${msg.role !== 'user' ? 'pr-8' : ''}`}">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
                {msg.sources && msg.sources.length > 0 && (
                <div className="mt-4 pt-2">
                  <h4 className="font-semibold text-xs text-brand-dark mb-1">
                    {t('askDoc.sourcesLabel')}
                  </h4>
                  {Array.from(
                    new Set(msg.sources.map((source) => source.page))
                  ).map((page, idx) => (
                    <u key={idx} className="text-brand-dark">
                      <li className="font-mono truncate">Page {page}</li>
                    </u>
                  ))}
                </div>
              )}
              </div>
              
            </div>
          </div>
        ))}
        {isAnswering && (
          <div className="flex justify-start">
            <div className="px-4 py-2 rounded-lg bg-slate-700 text-white">
              <Loader className="animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="p-4 border-slate-700">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          <div className="flex-1 flex items-center bg-brand-white rounded-md overflow-hidden border border-slate-600">
                        
                {/* Voice Input Button */}
                {isSupported && (
                    <button
                        type="button"
                        onClick={toggleListening}
                        className={`flex items-center justify-center p-1.5 m-1 rounded-sm transition-all
                            ${isListening 
                                ? 'bg-red-500 text-white animate-pulse' 
                                : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                            }`}
                        
                        aria-label={isListening ? "Stop listening" : "Start voice input"}
                        title={isListening ? "Stop listening" : "Start voice input"}
                    >
                        {isListening ? <IoStop size={16} /> : <IoMic size={16} />}
                    </button>
                )}

          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder={t('askDoc.inputPlaceholder')}
            className="flex-1 p-2 bg-transparent text-black border border-slate-600 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none"
            disabled={isAnswering}
            // ✅ This input will now retain focus across renders!
          />
          </div>
          <button
            type="submit"
            className="p-2.5 bg-brand-orange rounded-md text-white disabled:bg-slate-600 disabled:cursor-not-allowed"
            disabled={!userInput.trim() || isAnswering}
            aria-label={t('askDoc.sendButtonAria')}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatView;
